import { Fragment, useState, useEffect, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface Shortcut {
  keys: string[];
  label: string;
}

export function useKeyboardShortcuts() {
  const navigate = useNavigate();
  const [showHelp, setShowHelp] = useState(false);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const target = e.target as HTMLElement;
    const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

    if (e.key === '?' && !isInput && e.shiftKey) {
      e.preventDefault();
      setShowHelp(prev => !prev);
      return;
    }

    if (e.key === 'Escape') {
      setShowHelp(false);
      return;
    }

    if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
      e.preventDefault();
      const searchInput = document.querySelector<HTMLInputElement>('input[type="search"], input[placeholder*="Search"], input[placeholder*="بحث"], input[placeholder*="Ara"]');
      if (searchInput) {
        searchInput.focus();
        searchInput.select();
      }
      return;
    }

    if (e.altKey && !isInput) {
      switch (e.key) {
        case 'h':
          e.preventDefault();
          navigate('/');
          break;
        case 's':
          e.preventDefault();
          navigate('/shipments');
          break;
        case 'c':
          e.preventDefault();
          navigate('/contracts');
          break;
      }
    }
  }, [navigate]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return { showHelp, setShowHelp };
}

export function KeyboardShortcutsModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { t } = useTranslation();

  const shortcuts: Shortcut[] = [
    { keys: ['Ctrl', 'K'], label: t('shortcuts.focusSearch', 'Focus search bar') },
    { keys: ['Shift', '?'], label: t('shortcuts.showHelp', 'Show this help') },
    { keys: ['Esc'], label: t('shortcuts.closeModal', 'Close modal / dialog') },
    { keys: ['Alt', 'H'], label: t('shortcuts.gotoDashboard', 'Go to Dashboard') },
    { keys: ['Alt', 'S'], label: t('shortcuts.gotoShipments', 'Go to Shipments') },
    { keys: ['Alt', 'C'], label: t('shortcuts.gotoContracts', 'Go to Contracts') },
  ];

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-sm transform overflow-hidden rounded-xl bg-white shadow-xl transition-all">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                  <Dialog.Title as="h3" className="text-base font-semibold text-gray-900">
                    {t('shortcuts.title', 'Keyboard Shortcuts')}
                  </Dialog.Title>
                  <button onClick={onClose} className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
                <div className="px-5 py-4 space-y-3">
                  {shortcuts.map((shortcut, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{shortcut.label}</span>
                      <div className="flex items-center gap-1">
                        {shortcut.keys.map((key, ki) => (
                          <kbd
                            key={ki}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded"
                          >
                            {key}
                          </kbd>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
